import { useEffect, useState } from 'react';
import type { ApiClient } from '../api/api-client';
import { hasPermission } from '../access/access';
import { useAuth } from '../auth/use-auth';
import { getOrganizationMe } from '../../features/org-settings/api/org-settings.api';
import { PERM_ORGANIZATION_SETTINGS_VIEW } from '../../features/org-settings/model/org-settings.permissions';
import { fetchTenantBranches } from '../../features/organization-branches/api/organization-branches.api';
import { branchesTenantCanView } from '../../features/organization-branches/model/branches.permissions';
import { scheduleTimezoneFromRows } from '../../features/weekly-calendar/lib/calendar-config-from-org-me';
import { DEFAULT_SCHEDULE_TIMEZONE } from '../../features/weekly-calendar/utils/timeUtils';

/**
 * Эффективная таймзона текущей организации (+branch override) для рендера времени.
 * До ответа сетевых вызовов — дефолт (без «прыжка» UI), затем фактическая.
 * `GET /organizations/me` — только при `organization:settings:view` (согласовано с бэкендом);
 * branch override best-effort и только при праве на филиалы, иначе — org-таймзона или дефолт.
 */
export function useOrgTimezone(opts: {
  apiClient: ApiClient;
  organizationId: number | null;
  branchId?: number | null;
}): string {
  const { apiClient, organizationId, branchId } = opts;
  const { me } = useAuth();
  const capabilities = me?.capabilities;
  const canViewOrgSettings = hasPermission(capabilities, PERM_ORGANIZATION_SETTINGS_VIEW);
  const canViewBranches = branchesTenantCanView(capabilities);
  const hasBranchSource = canViewBranches && branchId != null && branchId > 0;
  const [timezone, setTimezone] = useState<string>(DEFAULT_SCHEDULE_TIMEZONE);

  useEffect(() => {
    if (organizationId == null || (!canViewOrgSettings && !hasBranchSource)) {
      setTimezone(DEFAULT_SCHEDULE_TIMEZONE);
      return;
    }
    let cancelled = false;
    setTimezone(DEFAULT_SCHEDULE_TIMEZONE);
    void (async () => {
      try {
        const orgRow = canViewOrgSettings ? await getOrganizationMe(apiClient) : null;
        let branchRow: Record<string, unknown> | null = null;
        if (hasBranchSource) {
          try {
            const branches = await fetchTenantBranches(apiClient);
            branchRow = (branches.find((b) => b.id === branchId) as Record<string, unknown> | undefined) ?? null;
          } catch {
            branchRow = null;
          }
        }
        if (!cancelled) {
          setTimezone(scheduleTimezoneFromRows(orgRow, branchRow));
        }
      } catch {
        if (!cancelled) {
          setTimezone(DEFAULT_SCHEDULE_TIMEZONE);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, branchId, canViewBranches, canViewOrgSettings, hasBranchSource, organizationId]);

  return timezone;
}